import React, { useEffect, useState } from "react";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import moment from "moment";

import caseStates from "@_configs/caseStates.config";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DateInput from "@_components/DateInput/DateInput";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import styles from "./CasesReportPopup.module.css";
import { useDispatch, useSelector } from "react-redux";
import { customerActions } from "@_actions/customer.actions";
import { catchError } from "@_utils/helpers";

const fieldGroups = [
  {
    id: "case-information",
    title: "Case Information",
    fields: [
      { key: "case_id", label: "Case ID" },
      { key: "source_case_id", label: "External Case ID" },
      { key: "case_state", label: "Status" },
      { key: "domain", label: "Domain" },
      { key: "country", label: "Country" },
      { key: "mail_flow", label: "Mail Flow" },
      { key: "image_filenames", label: "Image Filenames" },
      { key: "case_idle", label: "Case Idle" },
    ],
  },
  {
    id: "activity-history",
    title: "Activity History",
    fields: [
      { key: "case_creation_date", label: "Creation Date" },
      { key: "activation_date", label: "Activation Date" },
      { key: "state_change_date", label: "Status Change Date" },
      { key: "last_case_activity", label: "Last Case Activity" },
      { key: "login_count", label: "Login Count" },
      { key: "letters_sent", label: "Letters Sent" },
    ],
  },
  {
    id: "billing-and-costs",
    title: "Billing and Costs",
    fields: [
      { key: "payment_received", label: "Payment Received" },
      { key: "credits_used", label: "Credits Used" },
    ],
  },
];

const defaultCheckboxes = fieldGroups
  .flatMap((group) => group.fields)
  .reduce((acc, field) => ({ ...acc, [field.key]: true }), {});

// Some checkboxes map to multiple backend field names.
const fieldNameMap = {
  last_case_activity: ["last_case_activity_date", "last_case_activity_type"],
  payment_received: ["payment_amount_received", "payment_currency"],
};

function CasesReportPopup(props) {
  const dispatch = useDispatch();
  const sidebarReducer = useSelector((state) => state.sidebarReducer);
  const customerReducer = useSelector((state) => state.customerReducer);
  const { snackbarProps, showSnackbarError } = useSnackbar();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [caseState, setCaseState] = useState(sidebarReducer.status || "CASES");
  const [checkboxes, setCheckboxes] = useState(defaultCheckboxes);
  const [expandedGroups, setExpandedGroups] = useState(() =>
    fieldGroups.reduce((acc, group) => ({ ...acc, [group.title]: false }), {}),
  );

  useEffect(() => {
    setCaseState(sidebarReducer.status || "CASES");
  }, [sidebarReducer.status]);

  useEffect(() => {
    if (customerReducer.status.CASES_REPORT === "success") {
      dispatch(customerActions.clearStatusMessage("CASES_REPORT"));
      handleClose();
    } else if (catchError(customerReducer.status.CASES_REPORT)) {
      showSnackbarError(customerReducer.status.CASES_REPORT);
      dispatch(customerActions.clearStatusMessage("CASES_REPORT"));
    } else return;
    // eslint-disable-next-line
  }, [dispatch, customerReducer.status]);

  const handleClose = () => {
    setToDate("");
    setFromDate("");
    setCaseState(props.defaultCaseState || "CASES");
    setCheckboxes({ ...defaultCheckboxes });
    setExpandedGroups(
      fieldGroups.reduce(
        (acc, group) => ({ ...acc, [group.title]: false }),
        {},
      ),
    );
    props.close();
  };

  const handleGroupExpandToggle = (groupTitle) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupTitle]: !prev[groupTitle],
    }));
  };

  const handleCheckboxChange = (field) => {
    setCheckboxes((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleGroupToggle = (group) => {
    const fieldKeys = group.fields.map((field) => field.key);
    const allSelected = fieldKeys.every((key) => checkboxes[key]);

    setCheckboxes((prev) => {
      const next = { ...prev };
      fieldKeys.forEach((key) => {
        next[key] = !allSelected;
      });
      return next;
    });
  };

  const getGroupSelectionState = (group) => {
    const fieldKeys = group.fields.map((field) => field.key);
    const selectedCount = fieldKeys.filter((key) => checkboxes[key]).length;
    const totalCount = fieldKeys.length;

    return {
      selectedCount,
      totalCount,
      checked: selectedCount === totalCount,
      notAllSelected: selectedCount > 0 && selectedCount < totalCount,
    };
  };

  const handleDownload = () => {
    const selectedFields = Object.keys(checkboxes)
      .filter((key) => checkboxes[key])
      .flatMap((key) => fieldNameMap[key] || key);

    const reportData = {
      filter: {},
      fields: selectedFields,
      output_format: "excel",
    };

    if (fromDate) {
      reportData.filter.start_date = moment(fromDate).format("YYYY-MM-DD");
    }

    if (toDate) {
      reportData.filter.end_date = moment(toDate).format("YYYY-MM-DD");
    }
    if (caseState && caseState !== "CASES") {
      reportData.filter.case_state = caseState;
    }

    dispatch(customerActions.getCasesReport(reportData));
  };

  const totalFieldsCount = Object.keys(checkboxes).length;
  const selectedFieldsCount = Object.values(checkboxes).filter(Boolean).length;

  return (
    <DialogPopup
      title="Download case report"
      open={props.open}
      onClose={handleClose}
    >
      <div className={styles.content_container}>
        {/* Case Creation Date Section */}
        <div className={styles.section}>
          <h4 className={styles.section_title}>Case creation date</h4>
          <div className={styles.date_inputs}>
            <div className={styles.date_input_wrapper}>
              <DateInput
                label="From"
                value={fromDate}
                onChange={(value) => setFromDate(value)}
                className={styles.date_input}
                size="small"
              />
            </div>
            <div className={styles.date_input_wrapper}>
              <DateInput
                label="To"
                value={toDate}
                onChange={(value) => setToDate(value)}
                className={styles.date_input}
                size="small"
              />
            </div>
          </div>
          <p className={styles.description}> Dates in UTC</p>
        </div>

        <div className={styles.section}>
          <h4 className={styles.section_title}>Case state</h4>
          <FormControl className={styles.dropdown}>
            <Select
              variant="outlined"
              color="default"
              value={caseState}
              onChange={(e) => {
                setCaseState(e.target.value);
              }}
              size="small"
            >
              {Object.values(caseStates).map((state) => (
                <MenuItem key={state.key} value={state.key}>
                  {state.key === "CASES" ? "All" : state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className={styles.section}>
          <div className={styles.fields_header}>
            <h4 className={styles.section_title} style={{ margin: 0 }}>
              Fields
              <span className={styles.fields_summary}>
                {" "}
                ({selectedFieldsCount} of {totalFieldsCount} selected)
              </span>
            </h4>
          </div>

          <div className={styles.checkboxes_container}>
            <p className={styles.fields_subtitle}>
              Select the fields to include in the report.
            </p>
            {fieldGroups.map((group) => {
              const { selectedCount, totalCount, checked, notAllSelected } =
                getGroupSelectionState(group);
              const isExpanded = expandedGroups[group.title];

              return (
                <div key={group.title} className={styles.field_group}>
                  <div className={styles.field_group_header}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          indeterminate={notAllSelected}
                          onChange={() => handleGroupToggle(group)}
                          size="small"
                          data-testid={`group-checkbox-${group.id}`}
                        />
                      }
                      label={`${group.title} (${selectedCount}/${totalCount})`}
                      className={styles.group_checkbox}
                    />
                    <IconButton
                      onClick={() => handleGroupExpandToggle(group.title)}
                      className={styles.group_expand_button}
                      data-testid={`group-expand-toggle-${group.id}`}
                      size="small"
                    >
                      {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                    </IconButton>
                  </div>
                  {isExpanded && (
                    <div className={styles.field_group_items}>
                      {group.fields.map((field) => (
                        <FormControlLabel
                          key={field.key}
                          control={
                            <Checkbox
                              checked={!!checkboxes[field.key]}
                              onChange={() => handleCheckboxChange(field.key)}
                              size="small"
                              data-testid={`field-checkbox-${field.key}`}
                            />
                          }
                          label={field.label}
                          className={styles.field_checkbox}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <AppSnackbar {...snackbarProps} />
      <div className="action_container" style={{ paddingInlineEnd: "20px" }}>
        <Button
          className="btn_cancel"
          data-testid="cases-report-cancel-button"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          className="btn_confirm"
          data-testid="cases-report-download-button"
          onClick={handleDownload}
        >
          Download
        </Button>
      </div>
    </DialogPopup>
  );
}

export default CasesReportPopup;
